import { AnimationMixer, Clock, Group } from 'three';
import { clone as skeletonClone } from 'three/addons/utils/SkeletonUtils.js';
import WebGLGroup from '../_app/cuchillo/3D/WebGLGroup';
import ModelGLB from '../_app/cuchillo/3D/ModelGLB';
import TexturesController from '../_app/cuchillo/3D/TexturesController';
import { DRACO_LOADER } from '../3D/constants';
import { Metrics } from '../_app/cuchillo/core/Metrics';
import { Maths } from '../_app/cuchillo/utils/Maths';
import { Interaction } from '../_app/cuchillo/core/Interaction';
import { isTouch } from '../_app/cuchillo/core/Basics';
import CharacterAnimationController from './CharacterAnimationController';

// Parallax del mouse del character: inclina i desplaça el repulsionGroup segons la posició del
// mouse RESPECTE AL VIEWPORT (normalitzada a [-1,1] des del centre de la pantalla). Mateix TIPUS de
// moviment que la repulsió anterior (tilt + displaceX/Y + push en Z cap al centre), lerpat, però
// sense àrea/radi centrat al character. rotX/rotY en graus; displaceX/Y/Z en unitats de món.
export const CHARACTER_REPULSION = {
    enabled: true,
    rotX: 10,
    rotY: 10,
    displaceX: 20,
    displaceY: 20,
    displaceZ: 120,
    lerp: 0.04,
};

export default class Character extends ModelGLB {
    tick = 0;
    floatGroup = new WebGLGroup({ rotFactor: .05, zFactor: .1 });
    // Grup de repulsió del ratolí (mateix efecte que els thumbnails amb hasMouse): tilt +
    // desplaçament respecte el mouse. Embolcalla floatGroup perquè es compongui per sobre de
    // la posició/rotació/flotació, sense entrar a la branca hasMouse de WebGLObject (que
    // saltaria la rotació per scroll i usa un rotationGroup que el Character no munta).
    repulsionGroup = new Group();

    _clock = new Clock();
    _skinnedMesh  = null;
    _morphOverrides = {};
    _scene2 = null; // escena del model2 (tapon)
    // El parallax del mouse NO aplica fins que la intro de la Home acaba (Scene.enableCharacterMouse)
    // i es torna a desactivar quan comença una animació del character (cinemàtica animateGeneral).
    _mouseInputDisabled = true;

    constructor(opts = {}) {
        super(opts);
        this.model2       = opts.model2 || null;
        this.hasFlotation = opts.hasFlotation ?? true;
        this._hasRepulsion = opts.repulsion ?? false; // flag de repulsió del mouse (com els thumbnails)
        this.repulsionGroup.add(this.floatGroup);
        this.add(this.repulsionGroup);
    }

    init() {
        const loadGLB = (src, useSkeletonClone = false) => new Promise((resolve, reject) => {
            DRACO_LOADER.load(src, (gltf) => {
                resolve(useSkeletonClone ? { ...gltf, scene: skeletonClone(gltf.scene) } : gltf);
            }, undefined, reject);
        });

        const jobs = [loadGLB(this.model, true)];
        if (this.model2) jobs.push(loadGLB(this.model2, false));

        return Promise.all(jobs).then(([gltf1, gltf2]) => {
            // --- Model 1 (character) ---
            const scene1 = gltf1.scene;
            scene1.traverse(obj => {
                if (obj.isSkinnedMesh) {
                    obj.frustumCulled = false;
                    if (obj.morphTargetInfluences?.length) obj.material.morphTargets = true;
                    if (!this._skinnedMesh) this._skinnedMesh = obj;
                }
            });

            this.object = gltf1;
            this.mesh   = scene1.children[0];
            this.floatGroup.add(scene1);

            if (this._material) {
                scene1.traverse(obj => {
                    if (obj.isMesh) { obj.material = this._material.clone(); obj.material.needsUpdate = true; }
                });
            }


            // --- Model 2 (tapon) ---
            let taponMixer = null;
            let taponClips = [];
            if (gltf2) {
                this._scene2 = gltf2.scene;
                this.floatGroup.add(this._scene2);
                if (gltf2.animations.length) {
                    taponMixer = new AnimationMixer(this._scene2);
                    taponClips = gltf2.animations;
                }
            }

            // Inicia el controller amb els dos mixers i callbacks
            if (gltf1.animations.length) {
                CharacterAnimationController.init(
                    new AnimationMixer(scene1), gltf1.animations,
                    taponMixer, taponClips,
                    {
                        taponScene: this._scene2,
                        setMorph:   (name, value) => this.setMorphInfluence(name, value),
                        morphNames: this.morphTargetNames,
                    }
                );
            }

            this.size.copy(scene1.scale);
            this.resize();
            this.loadTextures();
        }).catch(e => { throw e; });
    }

    // --- Morph targets ---

    get morphTargetNames() {
        return this._skinnedMesh?.morphTargetDictionary
            ? Object.keys(this._skinnedMesh.morphTargetDictionary)
            : [];
    }

    setMorphInfluence(name, value) {
        this._morphOverrides[name] = value;
    }

    _applyMorphOverrides() {
        if (!this._skinnedMesh?.morphTargetDictionary) return;
        for (const [name, value] of Object.entries(this._morphOverrides)) {
            const idx = this._skinnedMesh.morphTargetDictionary[name];
            if (idx !== undefined) this._skinnedMesh.morphTargetInfluences[idx] = value;
        }
    }

    // --- Core ---

    loadTextures() {
        if (!this.mesh || !this.diffusion) return;

        TexturesController.load({
            src: this.diffusion,
            call: (texture) => {
                texture.flipY = false;
                this.object.scene.traverse(obj => {
                    if (obj.isMesh) {
                        if (!this._material) obj.material = obj.material.clone();
                        obj.material.map = texture;
                        if (obj.morphTargetInfluences?.length) obj.material.morphTargets = true;
                        obj.material.needsUpdate = true;
                    }
                });
            }
        });
    }

    update() {
        this.tick++;

        CharacterAnimationController.loop(this._clock.getDelta());
        this._applyMorphOverrides();

        if (this.hasFlotation) {
            this.floatGroup.rot.x = Math.sin(this.tick * .01) * .02;
            this.floatGroup.rot.y = Math.sin(this.tick * .007) * .04;
            this.floatGroup.rot.z = Math.sin(this.tick * .005) * .01;
            this.floatGroup.pos.y = Math.sin(this.tick * .02) * 4;
        }

        this.floatGroup.update();
        this._updateRepulsion();
        super.update();
    }

    // Parallax del mouse (CHARACTER_REPULSION): inclina i desplaça el repulsionGroup segons la
    // posició del mouse RESPECTE AL VIEWPORT (no un radi centrat al character), lerpat. Mateix
    // tipus de moviment que la repulsió anterior (tilt + displaceX/Y + push en Z cap al centre).
    // Quan la input del mouse està desactivada (_mouseInputDisabled: intro o cinemàtica), els
    // targets van a 0 → el grup torna suaument a repòs i deixa de reaccionar al ratolí.
    _updateRepulsion() {
        const s = CHARACTER_REPULSION;
        if (!this._hasRepulsion || !s.enabled || isTouch) return;

        // Mouse normalitzat a [-1,1] respecte al centre del viewport (0 quan està desactivat → repòs)
        let tnx = 0, tny = 0, targetPosZ = 0;
        if (!this._mouseInputDisabled) {
            tnx = Maths.clamp((Interaction.positions.mouse.x - Metrics.WIDTH * 0.5) / (Metrics.WIDTH * 0.5), -1, 1);
            tny = Maths.clamp((Interaction.positions.mouse.y - Metrics.HEIGHT * 0.5) / (Metrics.HEIGHT * 0.5), -1, 1);
            const dist = Math.sqrt(tnx * tnx + tny * tny);
            targetPosZ = -Maths.clamp(1 - dist, 0, 1) * s.displaceZ;
        }

        const targetRotX = Maths.toRadians(tny * s.rotX);
        const targetRotY = Maths.toRadians(tnx * s.rotY);
        const targetPosX = -tnx * s.displaceX;
        const targetPosY = tny * s.displaceY;

        const g = this.repulsionGroup;
        g.rotation.x = Maths.lerp(g.rotation.x, targetRotX, s.lerp);
        g.rotation.y = Maths.lerp(g.rotation.y, targetRotY, s.lerp);
        g.position.x = Maths.lerp(g.position.x, targetPosX, s.lerp);
        g.position.y = Maths.lerp(g.position.y, targetPosY, s.lerp);
        g.position.z = Maths.lerp(g.position.z, targetPosZ, s.lerp);
    }

    resize() {
        if (!this.object) return;
        let { x, y, width, height } = this.dom.getBoundingClientRect();

        // Fallback si DOM element sense dimensions (p.ex. fill de <canvas>)
        if (width === 0 || height === 0) {
            width  = Metrics.WIDTH  * 0.35;
            height = Metrics.WIDTH  * 0.35;
            x = (Metrics.WIDTH  - width)  * 0.5;
            y = (Metrics.HEIGHT - height) * 0.5;
        }

        const position = this.domPositionTo3D(x, y);
        this.position.x = position.x + width * 0.5;
        this.position.y = position.y - height * 0.5;
        this.pos.x = this.position.x;
        this.pos.y = this.position.y;

        const scale = this.sizeFactor ? width / this.sizeFactor : width / 20;
        this.object.scene.scale.set(scale, scale, scale);
        if (this._scene2) this._scene2.scale.set(scale, scale, scale);
        this.dataZ = 0;
    }

    dispose() {
        CharacterAnimationController.dispose();
        this._skinnedMesh = null;
        this._morphOverrides = {};
        if (this.object) this.floatGroup.remove(this.object.scene);
        if (this._scene2) { this.floatGroup.remove(this._scene2); this._scene2 = null; }
        this.repulsionGroup.remove(this.floatGroup);
        this.remove(this.repulsionGroup);
        super.dispose();
    }
}
